import { ShieldCheck, Wifi, Lock, Activity, UserCheck, ShieldAlert, KeyRound, MessageSquare } from "lucide-react";
import Link from "@/components/ui/RouterLink";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { ProcessSteps } from "@/components/ui/ProcessSteps";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const seo = pageSeo(
  "Security & Network — Protect Every Layer of Your Infrastructure",
  "Plan stronger network access, infrastructure protection and secure connectivity around your organisation's applications and digital operations."
);

const modules = [
  { icon: ShieldCheck, title: "Firewall Solutions", body: "Perimeter defenses configured around your network's access needs." },
  { icon: Wifi, title: "Network Protection", body: "Safeguards for the network layer your applications and services depend on." },
  { icon: Lock, title: "Secure Connectivity", body: "Encrypted, controlled connections between your systems and users." },
  { icon: Activity, title: "Infrastructure Monitoring", body: "Ongoing visibility into your infrastructure's health and activity." },
  { icon: UserCheck, title: "Access Management", body: "Control over who can reach which systems, and under what conditions." },
  { icon: ShieldAlert, title: "DDoS Protection", body: "Mitigation options to help your infrastructure stay available under attack traffic." },
  { icon: KeyRound, title: "VPN Solutions", body: "Secure remote access into your infrastructure for your team." },
  { icon: MessageSquare, title: "Security Consultation", body: "Talk through your current setup and where it may need strengthening." },
];

const process = [
  { n: "01", title: "Assess", body: "Understand the infrastructure, exposure and business requirements." },
  { n: "02", title: "Design", body: "Recommend an appropriate security and networking architecture." },
  { n: "03", title: "Implement", body: "Deploy the approved infrastructure and protection layers." },
  { n: "04", title: "Review", body: "Evaluate performance, access and ongoing requirements." },
];

export function SecurityNetworkPage() {
  useDocumentMeta(seo.title, seo.description);

  return (
    <>
      <PageHero
        eyebrow="Security & Network"
        title="Secure the Infrastructure Your Business Depends On"
        description="Plan stronger network access, infrastructure protection and secure connectivity around your organisation's applications and digital operations."
        primaryCta={{ label: "Request a Security Assessment", href: "/contact" }}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <SectionEyebrow>Solution Modules</SectionEyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            Security Built Around Your Infrastructure
          </h2>
          <p className="mt-4 text-sm text-text-secondary">
            The modules below are possible service categories — availability depends on your
            specific setup and is confirmed during an assessment.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {modules.map((m) => (
            <FeatureCard key={m.title} {...m} />
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-navy-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <SectionEyebrow>Our Process</SectionEyebrow>
            <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
              How a Security Engagement Works
            </h2>
          </div>
          <div className="mt-12">
            <ProcessSteps steps={process} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-20 text-center lg:px-8">
        <h2 className="font-display text-2xl font-semibold text-text-primary sm:text-3xl">
          Not Sure How Exposed Your Network Is?
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-text-secondary">
          We'll review your current setup and talk through where it could be strengthened —
          no assumptions, no scare tactics.
        </p>
        <div className="mt-7">
          <Link
            href="/contact"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
          >
            Request a Security Assessment
          </Link>
        </div>
      </section>
    </>
  );
}
